import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  effect,
  ChangeDetectionStrategy,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ProcessFlowService } from '../../services/process-flow.service';
import { ProcessDocumentService } from '../../../process-document-management/services/process-document.service';
import { CatalogService } from '@services/data/catalog.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { FlowDiagramComponent } from '../flow-diagram/flow-diagram.component';
import { FlowStepListComponent } from '../flow-step-list/flow-step-list.component';
import Swal from 'sweetalert2';
import {
  datePlusDays,
  discardChangesSwalOptions,
  ensureApprovedFinalStep,
  extractSaveError,
  FALLBACK_ROLES,
  findProcessByKey,
  FlowStepEditorModel,
  isFixedFinalIndex,
  isTerminalStepIndex,
  saveApplyOpenSuccessText,
  saveFlowSwalOptions,
  validateEditorFlows,
  validationMessage
} from '../../utils/status-flow-editor.utils';

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

@Component({
  selector: 'app-status-flow-editor',
  standalone: true,
  imports: [CommonModule, FlowDiagramComponent, FlowStepListComponent],
  templateUrl: './status-flow-editor.component.html',
  styleUrl: './status-flow-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class StatusFlowEditorComponent implements OnInit, OnDestroy {
  processes: any[] = [];
  selectedProcess: any = null;
  loadError = signal(false);
  availableStatuses = signal<any[]>([]);
  availableRoles = signal<any[]>([]);
  documentTypes = signal<any[]>([]);
  saveStatus = signal<SaveStatus>('idle');
  currentFlows: FlowStepEditorModel[] = [];

  private processService = inject(ProcessFlowService);
  private docService = inject(ProcessDocumentService);
  private catalogService = inject(CatalogService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  readonly appSettings = inject(AppSettings);

  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private savedClearTimer: ReturnType<typeof setTimeout> | null = null;
  private suppressAutosave = false;

  constructor() {
    effect(() => this.syncStatusesEffect());
    effect(() => this.syncRolesEffect());
    effect(() => this.syncDocTypesEffect());
  }

  private syncStatusesEffect(): void {
    const statuses = this.catalogService.statuses();
    this.availableStatuses.set((statuses || []).filter((s: any) => s.isActive !== false));
    if (this.currentFlows.length > 0 && statuses?.length) {
      this.relinkFlowStatuses();
      this.ensureApprovedFinal(false);
    }
  }

  private syncRolesEffect(): void {
    const roles = this.catalogService.roles();
    this.availableRoles.set(roles?.length ? roles : FALLBACK_ROLES);
  }

  private syncDocTypesEffect(): void {
    const docTypes = this.catalogService.documentTypes();
    if (!docTypes?.length) return;
    this.documentTypes.set(docTypes.filter((d: any) => d.isActive !== false));
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    if (this.savedClearTimer) clearTimeout(this.savedClearTimer);
  }

  loadData(): void {
    this.loadError.set(false);
    this.processService.getProcessesWithFlows().subscribe({
      next: (data) => this.onProcessesLoaded(data),
      error: () => this.clearSelectionAsError()
    });
    this.docService.getDocumentTypes().subscribe((types) => this.onDocTypesLoaded(types));
  }

  private processKeyFromRoute(): string {
    return String(this.route.snapshot.paramMap.get('processSlug') || '').trim();
  }

  private onProcessesLoaded(data: any[]): void {
    this.processes = data ?? [];
    const match = findProcessByKey(this.processes, this.processKeyFromRoute());
    if (!match) {
      this.clearSelectionAsError();
      return;
    }
    this.selectedProcess = match;
    this.loadProcessFlows(match);
  }

  private clearSelectionAsError(): void {
    this.selectedProcess = null;
    this.currentFlows = [];
    this.loadError.set(true);
  }

  private onDocTypesLoaded(types: any[]): void {
    this.documentTypes.set((types ?? []).filter((t: any) => t.isActive !== false));
  }

  closeEditor(): void {
    void this.router.navigate(['/configuracion/flujos']);
  }

  private resolveStatusIdFromFlow(f: any): any {
    if (f.statusId) return f.statusId;
    if (f.status?.id) return f.status.id;
    return this.findCatalogStatusId(f);
  }

  private findCatalogStatusId(f: any): any {
    const cat = this.catalogService.statuses() ?? [];
    const match = cat.find((s: any) =>
      (f.status?.uuid && s.uuid === f.status.uuid) || (f.status?.slug && s.slug === f.status.slug)
    );
    return match?.id ?? f.status?.code ?? null;
  }

  private relinkFlowStatuses(): void {
    let changed = false;
    for (const flow of this.currentFlows) {
      if (flow.statusId || !flow.status) continue;
      flow.statusId = this.resolveStatusIdFromFlow(flow);
      changed = true;
    }
    if (changed) this.currentFlows = [...this.currentFlows];
  }

  private loadProcessFlows(process: any): void {
    this.suppressAutosave = true;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveStatus.set('idle');
    this.currentFlows = this.mapProcessSteps(process);
    this.ensureApprovedFinal(false);
    queueMicrotask(() => { this.suppressAutosave = false; });
  }

  private mapProcessSteps(process: any): FlowStepEditorModel[] {
    if (!process) return [];
    const steps = (process.statusFlow || []).map((f: any) => FlowStepEditorModel.fromApi(f));
    steps.sort((a, b) => a.stepSequence - b.stepSequence);
    return steps;
  }

  /** Cierre fijo: último paso siempre APPROVED. */
  private approvedStatusRef(): any | null {
    return this.catalogService.getStatusBySlug('APPROVED')
      ?? this.availableStatuses().find((s) => String(s.slug || '').toUpperCase() === 'APPROVED')
      ?? null;
  }

  private ensureApprovedFinal(markDirty: boolean): void {
    const approved = this.approvedStatusRef();
    if (!approved && !this.currentFlows.length) return;
    const before = this.currentFlows.map((f) => `${f.statusSlug}:${f.stepSequence}`).join('|');
    this.currentFlows = ensureApprovedFinalStep(this.currentFlows, approved);
    const after = this.currentFlows.map((f) => `${f.statusSlug}:${f.stepSequence}`).join('|');
    if (markDirty && before !== after) this.markDirty();
  }

  addStep(): void {
    // Insertar antes del cierre fijo APPROVED
    this.ensureApprovedFinal(false);
    const insertAt = Math.max(0, this.currentFlows.length - 1);
    const step = FlowStepEditorModel.empty(insertAt + 1);
    this.currentFlows.splice(insertAt, 0, step);
    this.updateSequences();
    this.ensureApprovedFinal(false);
    this.markDirty();
  }

  removeStep(index: number): void {
    if (isFixedFinalIndex(this.currentFlows, index)) {
      Swal.fire('Atención', 'El paso final Aprobada (APPROVED) no se puede eliminar.', 'info');
      return;
    }
    this.currentFlows.splice(index, 1);
    this.updateSequences();
    this.ensureApprovedFinal(false);
    this.markDirty();
  }

  moveUp(index: number): void {
    if (index <= 0) return;
    if (isFixedFinalIndex(this.currentFlows, index)) return;
    this.swapSteps(index, index - 1);
  }

  moveDown(index: number): void {
    if (index >= this.currentFlows.length - 1) return;
    // No bajar un paso si el siguiente es el cierre fijo APPROVED
    if (isFixedFinalIndex(this.currentFlows, index + 1)) return;
    this.swapSteps(index, index + 1);
  }

  private swapSteps(a: number, b: number): void {
    const temp = this.currentFlows[a];
    this.currentFlows[a] = this.currentFlows[b];
    this.currentFlows[b] = temp;
    this.updateSequences();
    this.ensureApprovedFinal(false);
    this.markDirty();
  }

  private updateSequences(): void {
    this.currentFlows.forEach((flow, index) => { flow.stepSequence = index + 1; });
  }

  markDirty(): void {
    if (this.suppressAutosave || !this.selectedProcess) return;
    if (this.saveStatus() !== 'saving') this.saveStatus.set('pending');
  }

  hasUnsavedChanges(): boolean {
    return this.saveStatus() === 'pending';
  }

  async confirmDiscardChanges(): Promise<boolean> {
    const result = await Swal.fire(discardChangesSwalOptions());
    return result.isConfirmed;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = '';
  }

  private buildFlowPayload(): any[] | null {
    if (!this.selectedProcess) return null;
    const err = validateEditorFlows(this.currentFlows, (i) => this.isTerminalStep(i));
    if (err) {
      Swal.fire('Atención', validationMessage(err), 'warning');
      return null;
    }
    return this.mapFlowsToPayload();
  }

  private mapFlowsToPayload() {
    const lastIndex = this.currentFlows.length - 1;
    const defaultUntil = datePlusDays(30);
    return this.currentFlows.map((f, index) => {
      const terminal = this.currentFlows.length > 1 && index === lastIndex;
      return f.toPayload(terminal, defaultUntil);
    });
  }

  private applyLocalCatalog(payload: any[]): void {
    this.selectedProcess.statusFlow = payload.map((f) => this.enrichPayloadStep(f));
    this.syncProcessInLists();
  }

  private enrichPayloadStep(f: any): any {
    const st = this.availableStatuses().find((s) =>
      (f.statusUuid && s.uuid === f.statusUuid) || s.id === f.statusId
    ) ?? this.catalogService.getStatusById(f.statusId);
    const docType = this.documentTypes().find((d) => d.slug === f.documentTypeSlug);
    return { ...f, status: st, documentType: docType ?? null };
  }

  private syncProcessInLists(): void {
    const idx = this.processes.findIndex((p) => this.sameProcess(p, this.selectedProcess));
    if (idx >= 0) this.processes[idx] = this.selectedProcess;
    this.syncCatalogProcesses();
  }

  private syncCatalogProcesses(): void {
    const catalog = this.catalogService.processes();
    const catIdx = catalog.findIndex((p) => this.sameProcess(p, this.selectedProcess));
    if (catIdx < 0) return;
    const next = [...catalog];
    next[catIdx] = { ...next[catIdx], statusFlow: this.selectedProcess.statusFlow };
    this.catalogService.processes.set(next);
  }

  private sameProcess(a: any, b: any): boolean {
    return (a.uuid && a.uuid === b.uuid) || a.id === b.id;
  }

  async saveFlow(): Promise<void> {
    if (!this.canStartSave()) return;
    const payload = this.buildFlowPayload();
    if (!payload) return;
    const choice = await Swal.fire(saveFlowSwalOptions());
    if (choice.isDismissed) return;
    this.persistSave(payload, choice.isDenied);
  }

  private canStartSave(): boolean {
    if (!this.selectedProcess || this.saveStatus() === 'saving') return false;
    if (this.currentFlows.length) return true;
    Swal.fire('Atención', 'Agregue al menos un paso al flujo.', 'warning');
    return false;
  }

  private persistSave(payload: any[], applyToOpen: boolean): void {
    this.saveStatus.set('saving');
    const procKey = this.selectedProcess.uuid ?? this.selectedProcess.id;
    this.processService.saveStatusFlowBulk(procKey, payload, { applyToOpen }).subscribe({
      next: (res) => this.onSaveOk(payload, applyToOpen, res),
      error: (err) => this.onSaveFail(err)
    });
  }

  private onSaveOk(payload: any[], applyToOpen: boolean, res: any): void {
    this.applyLocalCatalog(payload);
    this.saveStatus.set('saved');
    if (applyToOpen) this.showApplyOpenSuccess(res?.affectedCount ?? 0);
    this.scheduleSavedIdle();
  }

  private showApplyOpenSuccess(affectedCount: number): void {
    Swal.fire({
      icon: 'success',
      title: 'Flujo guardado',
      text: saveApplyOpenSuccessText(affectedCount),
      timer: 2800,
      showConfirmButton: false
    });
  }

  private scheduleSavedIdle(): void {
    if (this.savedClearTimer) clearTimeout(this.savedClearTimer);
    this.savedClearTimer = setTimeout(() => this.clearSavedStatus(), 2000);
  }

  private clearSavedStatus(): void {
    if (this.saveStatus() === 'saved') this.saveStatus.set('idle');
  }

  private onSaveFail(err: any): void {
    this.saveStatus.set('error');
    Swal.fire('Error', 'No se pudo guardar el flujo: ' + extractSaveError(err), 'error');
  }

  isTerminalStep(index: number): boolean {
    return isTerminalStepIndex(this.currentFlows.length, index);
  }
}
